import { useCallback, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { Combobox, type ComboboxOption } from '@opendoor/atoms/combobox';
import { Popover } from '@opendoor/atoms/popover';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import {
  isListingSortSlug,
  listingSortOptions,
  type ListingSortSlug,
} from 'components/marketplace/helpers/sortOptions';
import { PopoverRadioOptions } from 'components/marketplace/shared/PopoverRadioOptions';
import {
  MARKETPLACE_POPOVER_SURFACE_CLASS,
  type PopoverTriggerRender,
} from 'components/marketplace/shared/popoverStyles';

interface ListingSortControlProps {
  /** Currently applied sort slug. */
  value: ListingSortSlug;
  /** Runs a fresh search for the chosen sort. */
  onChange: (sort: ListingSortSlug) => void;
  /** Extra classes for the wrapping control. */
  className?: string;
  /** Distinguishes the concurrently mounted mobile and desktop instances. */
  analyticsName?: string;
}

const COMBOBOX_OPTIONS: ComboboxOption[] = listingSortOptions;

export const ListingSortControl = ({
  value,
  onChange,
  className,
  analyticsName,
}: ListingSortControlProps) => {
  const handleValueChange = useCallback(
    (option: ComboboxOption | null) => {
      if (option != null && isListingSortSlug(option.value)) {
        onChange(option.value);
      }
    },
    [onChange],
  );

  return (
    <Combobox
      aria-label="Sort homes"
      analyticsName={analyticsName}
      trackImpression={analyticsName != null}
      size="sm"
      placeholder="Sort"
      options={COMBOBOX_OPTIONS}
      className={className}
      value={COMBOBOX_OPTIONS.find((option) => option.value === value) ?? null}
      onValueChange={handleValueChange}
    />
  );
};

export const DesktopListingSortControl = ({
  value,
  onChange,
  className,
  analyticsName,
}: ListingSortControlProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption =
    listingSortOptions.find((option) => option.value === value) ?? listingSortOptions[0];

  const handleOptionClick = useCallback(
    (nextSort: ListingSortSlug) => {
      onChange(nextSort);
      setIsOpen(false);
    },
    [onChange],
  );

  const renderTrigger = useCallback<PopoverTriggerRender>(
    (props) => (
      <Button
        {...props}
        analyticsName={analyticsName ?? 'marketplace-sort'}
        aria-label={`Sort homes: ${selectedOption.label}`}
        trackImpression={analyticsName != null}
        size="sm"
        variant="secondary"
        className={cn(
          'h-10 min-w-0 shrink-0 rounded-lg border-transparent bg-transparent px-2',
          'not-disabled:hover:border-transparent not-disabled:hover:bg-od-bg-hover-primary',
          'not-disabled:active:border-transparent not-disabled:active:bg-od-bg-hover-primary',
          'data-[state=open]:border-transparent data-[state=open]:bg-od-bg-hover-primary',
          className,
        )}
      >
        <Text as="span" className="text-body-sm text-od-content-tertiary">
          Sort:
        </Text>
        <Text as="span" className="whitespace-nowrap text-body-sm text-od-content-primary">
          {selectedOption.label}
        </Text>
        <IconChevronDown size={16} aria-hidden />
      </Button>
    ),
    [analyticsName, className, selectedOption.label],
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger render={renderTrigger} />
      <Popover align="end" size="sm" className={cn('w-56', MARKETPLACE_POPOVER_SURFACE_CLASS)}>
        <Popover.Title className="sr-only">Sort homes</Popover.Title>
        <PopoverRadioOptions
          aria-label="Sort homes"
          analyticsName={analyticsName ?? 'marketplace-sort'}
          options={listingSortOptions}
          value={value}
          onValueChange={handleOptionClick}
        />
      </Popover>
    </Popover.Root>
  );
};
